// O ajuste mês a mês de um alvo da EAP (design D20, spec target-month-adjustments): as contas
// puras da tabela de edição. Meses em "AAAA-MM", dinheiro em centavos inteiros, nada de
// `Date` além de `currentCompetence` (que só destaca os meses passados).
//
// A série editada é um `Map<mês, centavos>` com a curva INTEIRA do alvo: é o que vai para o
// servidor (`editedSeries`), que confere o saldo, a janela e o resultado pelo motor de novo.
import { compareMonthKey, shiftMonth } from "./hierarchy";

const COMPETENCE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** `[["AAAA-MM", centavos]]` (a forma da rota) → `Map`, sem zeros. */
export const seriesMap = (pairs) => {
  const map = new Map();
  for (const pair of Array.isArray(pairs) ? pairs : []) {
    const [month, cents] = Array.isArray(pair)
      ? pair
      : [pair?.month ?? pair?.competence, pair?.cents];
    if (
      !COMPETENCE.test(String(month ?? "")) ||
      !Number.isSafeInteger(cents) ||
      cents === 0
    )
      continue;
    map.set(month, (map.get(month) ?? 0) + cents);
  }
  return map;
};

/** `Map` → `[["AAAA-MM", centavos]]` em ordem de mês, sem zeros. */
export const seriesPairs = (map) =>
  [...(map ?? new Map())]
    .filter(([, cents]) => cents !== 0)
    .sort(([left], [right]) => compareMonthKey(left, right));

const nonZeroMonths = (map) =>
  [...(map ?? new Map())]
    .filter(([, cents]) => cents !== 0)
    .map(([month]) => month);

/** Cada mês de `first` a `last`, inclusive. */
export const monthsBetween = (first, last) => {
  const months = [];
  if (
    !COMPETENCE.test(String(first ?? "")) ||
    !COMPETENCE.test(String(last ?? ""))
  )
    return months;
  for (
    let month = first;
    compareMonthKey(month, last) <= 0;
    month = shiftMonth(month, 1)
  ) {
    months.push(month);
    // Guarda contra uma janela absurda vinda de fora: 50 anos de linhas não é uma tabela.
    if (months.length > 600) break;
  }
  return months;
};

/**
 * As linhas da tabela: contínuas do primeiro ao último mês com valor (na referência, na curva
 * atual ou na editada), incluindo os meses vazios no meio, mais `extraMonths` depois do
 * último — sem passar do fim da janela.
 */
export const tableMonths = ({
  reference,
  current,
  edited,
  window,
  extraMonths = 0,
}) => {
  const months = [reference, current, edited]
    .flatMap(nonZeroMonths)
    .sort(compareMonthKey);
  if (months.length === 0) return [];
  let last = months[months.length - 1];
  for (let added = 0; added < extraMonths; added += 1) {
    const next = shiftMonth(last, 1);
    if (window?.last && compareMonthKey(next, window.last) > 0) break;
    last = next;
  }
  return monthsBetween(months[0], last);
};

/** O mês seguinte, ou `null` quando ele passa do fim da janela. */
export const nextMonthWithin = (month, window) => {
  const next = shiftMonth(month, 1);
  return window?.last && compareMonthKey(next, window.last) > 0 ? null : next;
};

/** Move `cents` de um mês para outro (nunca mais do que o mês tem). */
export const moveBetween = (edited, from, to, cents) => {
  const available = edited.get(from) ?? 0;
  const amount = Math.min(Math.max(0, cents), Math.max(0, available));
  if (amount === 0 || from === to) return edited;
  const next = new Map(edited);
  next.set(from, available - amount);
  next.set(to, (next.get(to) ?? 0) + amount);
  return next;
};

/** "Adiar para o mês seguinte": o mês inteiro vai para o próximo. */
export const postponeMonth = (edited, month, window) => {
  const next = nextMonthWithin(month, window);
  return next === null
    ? edited
    : moveBetween(edited, month, next, edited.get(month) ?? 0);
};

const total = (map) =>
  [...(map ?? new Map())].reduce((sum, [, cents]) => sum + cents, 0);

/** O saldo a redistribuir: o que falta (> 0) ou sobra (< 0) para a série fechar com a referência. */
export const balanceOf = (reference, edited) =>
  total(reference) - total(edited);

/** `Map<mês, editado − referência>`, só os meses que mudaram. */
export const deltaOf = (reference, edited) => {
  const delta = new Map();
  const months = new Set([
    ...nonZeroMonths(reference),
    ...nonZeroMonths(edited),
  ]);
  for (const month of months) {
    const difference = (edited.get(month) ?? 0) - (reference.get(month) ?? 0);
    if (difference !== 0) delta.set(month, difference);
  }
  return delta;
};

export const sameSeries = (left, right) => deltaOf(left, right).size === 0;

/**
 * O que um conjunto de movimentos (as entradas ativas de um alvo) faz com a curva: quantos
 * meses mudam e quanto sai de um mês. É o resumo da linha do alvo na lista e na EAP.
 */
export const adjustmentSummary = (entries) => {
  const net = new Map();
  let exceedsCents = 0;
  for (const entry of entries ?? []) {
    if (!Number.isSafeInteger(entry?.amountCents)) continue;
    net.set(
      entry.fromCompetence,
      (net.get(entry.fromCompetence) ?? 0) - entry.amountCents,
    );
    net.set(
      entry.toCompetence,
      (net.get(entry.toCompetence) ?? 0) + entry.amountCents,
    );
    if (Number.isSafeInteger(entry?.exceedsCents))
      exceedsCents += entry.exceedsCents;
  }
  const changed = [...net].filter(([, cents]) => cents !== 0);
  return {
    entryCount: (entries ?? []).length,
    monthCount: changed.length,
    movedCents: changed.reduce(
      (sum, [, cents]) => sum + (cents > 0 ? cents : 0),
      0,
    ),
    exceedsCents,
  };
};

/** As entradas ativas de ajuste de mês agrupadas por nó: `Map<wbsCodeId, entradas[]>`. */
export const adjustmentsByNode = (adjustments) => {
  const byNode = new Map();
  for (const entry of adjustments ?? []) {
    if (entry?.active === false || !entry?.wbsCodeId) continue;
    const list = byNode.get(entry.wbsCodeId);
    if (list) list.push(entry);
    else byNode.set(entry.wbsCodeId, [entry]);
  }
  return byNode;
};

export const monthsText = (count) =>
  `${count} ${count === 1 ? "mês" : "meses"}`;

/** A competência de hoje, no fuso do navegador: só para destacar os meses que já passaram. */
export const currentCompetence = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

/** `"1.500,00"` → `150000`; `"0"`/`"0,00"` → `0`; o resto → `NaN`. */
export const parseReaisToCents = (input) => {
  const text = String(input ?? "").trim();
  if (/^0+(,0*)?$/.test(text)) return 0;
  if (!/^\d{1,3}(\.\d{3})*(,\d{0,3})?$|^\d+(,\d{0,3})?$/.test(text)) return NaN;
  const [whole, fraction = ""] = text.replace(/\./g, "").split(",");
  const padded = `${fraction}000`.slice(0, 3);
  const cents =
    Number(whole) * 100 +
    Number(padded.slice(0, 2)) +
    (Number(padded.slice(2, 3)) >= 5 ? 1 : 0);
  return Number.isSafeInteger(cents) ? cents : NaN;
};

/** `150000` → `"1500,00"` (o texto que o campo mostra). */
export const centsToReaisInput = (cents) =>
  (cents / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });

/**
 * R$ sem passar pelo contexto do projeto (`money()` lê contexto por dentro e não pode ser
 * chamado um número variável de vezes por render). O ajuste é sempre em reais.
 */
export const reais = (cents) =>
  Number.isFinite(cents)
    ? (cents / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";
